/**
 * Adding a `show: false` column to a table is not enough to keep it hidden. The presenter reads a
 * saved `hidden_columns` list in preference to the column defaults, and that list predates the new
 * column — so for anyone who has ever toggled a column, the new one appears visible.
 *
 * A migration appends its column ids to a saved list exactly once, and is recorded as applied even
 * when there is no saved list: a user who later shows one of those columns on purpose must not have
 * it hidden again on the next load.
 *
 * Kept free of `@enter/core` so it can be unit-tested without a DOM.
 */
export interface IHiddenColumnsMigration {
    id: string;
    columnIds: string[];
}

export interface IHiddenColumnsMigrationResult {
    // Undefined when there is no saved list: the column defaults already hide the new columns.
    hiddenColumns: string[] | undefined;
    appliedMigrations: string[];
}

export function migrateHiddenColumns(
    savedHiddenColumns: string[] | undefined,
    appliedMigrations: string[] | undefined,
    migration: IHiddenColumnsMigration,
): IHiddenColumnsMigrationResult | undefined {
    const applied = appliedMigrations || [];
    if (applied.includes(migration.id)) {
        return undefined;
    }

    const hiddenColumns = savedHiddenColumns
        ? [...savedHiddenColumns, ...migration.columnIds.filter(id => !savedHiddenColumns.includes(id))]
        : undefined;

    return {
        hiddenColumns,
        appliedMigrations: [...applied, migration.id],
    };
}
